import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import deepCopy from 'lib/deepCopy';
import {convertMethodToMethodNode} from 'procedures/design/flowchartManagementUtils';
import {MethodUpdater} from 'procedures/design/LegacyProcedureEditor';
import useValidateProcedure from 'procedures/design/method-config/useValidateProcedure';
import {
  ProcedureValidationContext,
  ProcedureValidationContextValue,
} from 'procedures/design/validation/ProcedureValidationContext';
import {Method} from 'procedures/ProcedureDomainTypes';
import MethodSidepanelContext from 'procedures/processes/MethodSidepanelContext';
import {ProcedurePauseCondition} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';
import {useFlowchartSidepanelTabUnsavedChanges} from 'ui-library/reactflow/sidepanels/sidepanel-manager/useFlowchartSidepanelTabUnsavedChanges';

import {MethodEditorTab} from './MethodConfigConstants';

export interface UseDraftMethodEditorParams {
  method: Method;
  onSave: (method: Method) => Promise<void>;
  pauseCondition: ProcedurePauseCondition;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  procedureType: ProcedureType;
}

export interface UseDraftMethodEditorReturn {
  isEditing: boolean;
  onStartEditing: () => void;
  onSave: () => Promise<void>;
  onCancel: () => void;
  onUpdateMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  methodDuringEditing: Method;
  newValidationContextValue: ProcedureValidationContextValue;
}

export interface UseValidationDraftStateParams {
  draftMethod: Method | null;
  procedureType: ProcedureType;
}

/**
 * Local helper hook to run validations against a draft method. It returns a full validation context value,
 * which the calling component will need to pass into ProcedureValidationContext.Provider.
 *
 * Exported only for testing.
 *
 * @package
 */
export function useValidateDraftState({
  draftMethod,
  procedureType,
}: UseValidationDraftStateParams): ProcedureValidationContextValue {
  // The useValidate hook needs a full flowchart, so we convert our single method into a flowchart. Also,
  // the hook needs the flowchart to be stable to prevent infinite re-renders, so we memoize it here.
  const draftFlowchartForValidation = useMemo(
    () => (draftMethod ? {nodes: [convertMethodToMethodNode(draftMethod)], edges: []} : null),
    [draftMethod]
  );

  const draftProcedureValidationErrors = useValidateProcedure(
    draftMethod ? draftFlowchartForValidation : null,
    procedureType,
    null
  );

  // During edits, we override the errors to show only those associated with the draft method. This will
  // temporarily suppress other errors (e.g., steps with duplicate names), but only during editing.
  const isEditing = draftMethod != null;
  const parentContextValue = ProcedureValidationContext.useContext();
  return useMemo(
    () =>
      isEditing
        ? {...parentContextValue, procedureValidationErrors: draftProcedureValidationErrors}
        : parentContextValue,
    [isEditing, parentContextValue, draftProcedureValidationErrors]
  );
}

/**
 * This hook is a simple draft-state manager for MODAL edits of a method. "Modal" means it has clearly
 * defined start and end actions. And "end actions" includes both save and cancel options.
 *
 * In practice, this is used when editing a method's Definitions and Test Orders.
 *
 * The hook keeps track of the method data prior to initiating edits, so we can revert if the user cancels.
 * It also manages the unsavedChanges hook, which the workflows platform offers as a way to ensure users
 * don't lose unsaved changes when tabbing away or closing the sidepanel.
 *
 * We also temporarily pause the autosaver while editing is in progress.
 *
 * NOTE: This hook is only usable in the PoW method editor. Legacy procedures has its own strategy for this.
 */
export default function useDraftMethodEditor({
  method,
  onSave: parentOnSave,
  onChangeMethod: parentOnChangeMethod,
  pauseCondition,
  procedureType,
}: UseDraftMethodEditorParams): UseDraftMethodEditorReturn {
  const [isEditing, setIsEditing] = useState(false);
  const [draftMethod, setDraftMethod] = useState<Method | null>(null);
  const methodPriorToOpeningEditor = useRef<Method | null>(null);
  const {pauseAutosaver} = MethodSidepanelContext.useContext();

  assert(pauseAutosaver != null, 'useDraftMethodEditor can only run in a PoW context');

  /**
   * Our onSave handler is really only for managing the pause control. It can be called directly (when
   * the user presses Save) or indirectly (by the unsavedChanges hook below)
   */
  const handleOnSave = useCallback(async () => {
    await parentOnSave(draftMethod!);
    setIsEditing(false);
    setDraftMethod(null);
    pauseAutosaver(pauseCondition, false);
  }, [parentOnSave, draftMethod, pauseAutosaver, pauseCondition]);

  /**
   * Our onDiscard handler mainly manages reverting to the old method data
   */
  const _handleOnDiscard = useCallback(() => {
    setIsEditing(false);
    setDraftMethod(null);

    parentOnChangeMethod(method.cid, () => {
      const revertTo = methodPriorToOpeningEditor.current!;
      methodPriorToOpeningEditor.current = null;
      pauseAutosaver(pauseCondition, false);
      return revertTo;
    });
  }, [parentOnChangeMethod, pauseAutosaver, pauseCondition, method.cid]);

  // This hook is provided by workflows, and presents a confirmation modal if the user tries to navigate
  // away with unsaved changes.
  const {setIsTabDirty} = useFlowchartSidepanelTabUnsavedChanges(
    MethodEditorTab.DEFINITIONS,
    'Method Definitions',
    _handleOnDiscard
  );

  const handleOnCancel = useCallback(() => {
    _handleOnDiscard();
    // We need to set the tab dirty state to false when cancelling but staying in the tab.
    // We can't call setIsTabDirty within the callback we pass to useFlowchartSidepanelTabUnsavedChanges
    // because that would create a circular dependency. We also don't need to worry about the tab dirty state
    // in the callback we pass to the hook because the tab gets deleted immediately after the callback gets called.
    setIsTabDirty(false);
  }, [setIsTabDirty, _handleOnDiscard]);

  const handleOnStartEditing = useCallback(() => {
    pauseAutosaver(pauseCondition, true);
    setIsEditing(true);

    const methodForOnCancel = deepCopy(method);
    methodPriorToOpeningEditor.current = methodForOnCancel;
    setDraftMethod(methodForOnCancel);

    // This notion of dirty is not the same as the procedure's internal dirty state. It only answers
    // the question: has the user made any change to this tab since starting the edit operation?
    setIsTabDirty(false);
  }, [method, pauseAutosaver, pauseCondition, setIsTabDirty]);

  const handleOnUpdateMethod = useCallback(
    (cid: string, methodUpdater: MethodUpdater): void => {
      // Track in local state while editing
      if (isEditing && draftMethod != null) {
        setDraftMethod(methodUpdater(draftMethod));
        setIsTabDirty(true);
      }
      parentOnChangeMethod(cid, methodUpdater);
    },
    [isEditing, draftMethod, parentOnChangeMethod, setIsTabDirty]
  );

  const newValidationContextValue = useValidateDraftState({draftMethod, procedureType});

  // Cleanup on unmount
  useEffect(
    () => () => {
      pauseAutosaver(pauseCondition, false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    isEditing,
    onStartEditing: handleOnStartEditing,
    onSave: handleOnSave,
    onCancel: handleOnCancel,
    onUpdateMethod: handleOnUpdateMethod,
    methodDuringEditing: draftMethod ?? method,
    newValidationContextValue,
  };
}
